
import { useState, useCallback, useEffect } from "react";
import { Board } from "../types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/**
 * Hook for querying and managing boards data
 */
export const useBoardsQuery = (userId: string | undefined) => {
  const [boards, setBoards] = useState<Board[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch boards from the database
  const fetchBoards = useCallback(async () => {
    if (!userId) {
      setBoards([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("boards")
        .select("*")
        .eq("user_id", userId)
        .order("display_order", { ascending: true });

      if (error) throw error;

      console.log("Boards fetched:", data);
      
      // Map DB fields to our Board type
      const mappedBoards: Board[] = data.map(item => ({
        id: item.id,
        title: item.title,
        description: item.description,
        color: item.color,
        display_order: item.display_order,
        user_id: item.user_id,
        created_at: item.created_at,
        updated_at: item.updated_at
      }));
      
      setBoards(mappedBoards);
    } catch (error) {
      console.error("Error fetching boards:", error);
      toast.error("Failed to load boards");
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Refresh boards data
  const refreshBoards = useCallback(() => {
    fetchBoards();
  }, [fetchBoards]);

  // Get a board by ID
  const getBoardById = useCallback(
    (id: string) => boards.find((board) => board.id === id),
    [boards]
  );

  // Update a board's position
  const updateBoardPosition = useCallback(
    async (boardId: string, targetPosition: number): Promise<boolean> => {
      if (!userId) return false;

      try {
        // Get the board to move
        const sourceBoard = getBoardById(boardId);
        if (!sourceBoard) {
          console.error("Source board not found:", boardId);
          return false;
        }

        // First update the position locally
        let updatedBoards = [...boards];
        
        // Remove the source board
        updatedBoards = updatedBoards.filter(board => board.id !== boardId);
        
        // Add it back at the target position
        updatedBoards.splice(targetPosition, 0, sourceBoard);
        
        // Update positions on all boards
        updatedBoards = updatedBoards.map((board, index) => ({
          ...board,
          display_order: index
        }));
        
        // Update local state first for immediate UI feedback
        setBoards(updatedBoards);
        
        // Then update in database manually because update_board_positions RPC function doesn't exist yet
        const updatePromises = updatedBoards.map(board => 
          supabase
            .from('boards')
            .update({ display_order: board.display_order })
            .eq('id', board.id)
        );
        
        await Promise.all(updatePromises);
        return true;
      } catch (error) {
        console.error("Error in updateBoardPosition:", error);
        refreshBoards();
        return false;
      }
    },
    [boards, getBoardById, refreshBoards, userId]
  );

  // Initial fetch
  useEffect(() => {
    fetchBoards();
  }, [fetchBoards]);

  return {
    boards,
    isLoading,
    refreshBoards,
    getBoardById,
    updateBoardPosition
  };
};
